import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';

import { untilDestroyed } from '@app/core';
import { MfaEnum, User } from '@app/models';
import { ErrorHandlerService, MfaService, UserService } from '@app/services';
import { AlertModalService, SetMissingUserInfoModalService, ConfirmOtpModalService } from '@app/shared';
import { EditEmailModalService } from './edit-email-modal/edit-email-modal.service';
import { EditPasswordModalService } from './edit-password-modal/edit-password-modal.service';
import { AddMfaModalService } from './add-mfa-modal/add-mfa-modal.service';

@Component({
  selector: 'app-configurations-security',
  templateUrl: './configurations-security.component.html',
  styleUrls: ['../configurations.scss', './configurations-security.component.scss'],
  standalone: false
})
export class ConfigurationsSecurityComponent implements OnInit, OnDestroy {
  currentUser: User;
  isLoading = false;

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private alertModalService: AlertModalService,
    private errorHandlerService: ErrorHandlerService,
    private userService: UserService,
    private mfaService: MfaService,
    private setMissingUserInfoModalService: SetMissingUserInfoModalService,
    private editEmailModalService: EditEmailModalService,
    private editPasswordModalService: EditPasswordModalService,
    private addMfaModalService: AddMfaModalService,
    private confirmOtpModalService: ConfirmOtpModalService
  ) {}

  ngOnInit() {
    this.userService
      .watchCurrentUser()
      .pipe(
        filter(user => !!user),
        untilDestroyed(this)
      )
      .subscribe({ next: user => (this.currentUser = user) });
  }

  ngOnDestroy() {}

  openSetMissingUserInfoModal() {
    this.setMissingUserInfoModalService
      .openIfMissingUserInfo({ ignoreLocale: true, ignoreCpf: true, ignoreBirthdate: true })
      .pipe(tap(() => this.userService.getCurrentUser({ fetchPolicy: 'network-only' }).subscribe()))
      .subscribe({ error: () => this.router.navigate(['/']) });
  }

  openEditEmailModal() {
    this.editEmailModalService.open({ user: this.currentUser }).subscribe();
  }

  openEditPasswordModal() {
    this.editPasswordModalService.open().subscribe();
  }

  openAddMfaModal() {
    this.addMfaModalService
      .open()
      .pipe(tap(() => this.userService.getCurrentUser({ fetchPolicy: 'network-only' }).subscribe()))
      .subscribe();
  }

  deleteMfaItem() {
    this.isLoading = true;
    this.alertModalService
      .warning({
        text: this.translateService.instant('alerts.deleteMfa'),
        confirmButtonText: this.translateService.instant('button.exclude')
      })
      .pipe(
        switchMap(() => this.confirmOtpModalService.open()),
        switchMap(otp => (otp ? this.mfaService.disableTOTP({ otp }) : of(null))),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        error: error => this.errorHandlerService.handle(error)
      });
  }
}
